import { Button, TextField } from '@schuettflix/react-components';
import { useTranslation } from 'react-i18next';
import { WizardPanel } from '@/shared/components/Wizard.tsx';
import React from 'react';
import { Controller, FieldError } from 'react-hook-form';
import {
    invalidPhoneNumberKey,
    missingCountryCodeKey,
    missingPhoneNumberKey,
    PhoneFormSchema,
    PhoneInput,
} from '@/modules/location/components/phoneinput/PhoneInput.tsx';
import { MarketCodeSchema } from '@schuettflix/utils';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';
import { getCountryCodeByIsoCode } from '@/modules/location/components/phoneinput/CountryCodes.ts';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { RoleSelector } from '@/modules/location/components/roleselector/RoleSelector.tsx';
import { ContactPerson, ContactPersonSchema } from '@/modules/location/domain/ContactPersonSchema.ts';
import { z } from 'zod';
import { RoleSchema } from '@/modules/location/components/roleselector/Roles.ts';

const ContactPersonFormSchema = z.object({
    name: z.string().min(1),
    phone: PhoneFormSchema,
    role: RoleSchema.nullable().pipe(RoleSchema),
});

type ContactPanelProps = {
    value: ContactPerson | null;
    onClose: () => void;
    onSubmit: (contact: ContactPerson | null) => void;
};

export const ContactPanel = (props: ContactPanelProps) => {
    const { t } = useTranslation();
    const { marketCode } = useMarketContext();

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useZodForm({
        schema: ContactPersonFormSchema.pipe(ContactPersonSchema),
        defaultValues: props.value ?? {
            name: '',
            role: null,
            phone: {
                number: null,
                countryCode: getCountryCodeByIsoCode(marketCode),
            },
        },
    });

    const getErrorMessage = (fieldName: 'name' | 'role' | 'phone', error?: FieldError) => {
        if (!error) return;

        const typeTranslationMap: Record<'name' | 'role' | 'phone', Record<string, string>> = {
            name: {
                too_small: t('contactPerson.errors.missingName'),
            },
            phone: {
                invalid_type: t('contactPerson.errors.missingPhoneNumber'),
            },
            role: {
                invalid_type: t('contactPerson.errors.missingRole'),
            },
        };

        const messageTranslationMap: Record<string, string> = {
            [missingPhoneNumberKey]: t('contactPerson.errors.missingPhoneNumber'),
            [missingCountryCodeKey]: t('contactPerson.errors.missingCountryCode'),
            [invalidPhoneNumberKey]: t('contactPerson.errors.invalidPhoneNumber'),
        };

        return (
            typeTranslationMap[fieldName]?.[error.type] ||
            (error.message ? messageTranslationMap[error.message] : undefined) ||
            error.type
        );
    };

    const onSubmit = handleSubmit(props.onSubmit);

    return (
        <WizardPanel
            noMaximize
            onClose={props.onClose}
            title={t('contactPerson.panel.title')}
            footer={<ContactPanelFooter onClick={onSubmit} />}
        >
            <form onSubmit={onSubmit}>
                <h3 className="font-copy-md-strong mb-2">{t('contactPerson.infoText')}</h3>

                <div className="flex flex-col gap-4">
                    <TextField
                        variant="sm"
                        placeholder={t('contactPerson.panel.name.label')}
                        {...register('name')}
                        errorMessage={getErrorMessage('name', errors?.name)}
                    />
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                            <PhoneInput
                                placeholder={t('contactPerson.panel.phoneNumber.label')}
                                {...field}
                                errorMessage={getErrorMessage('phone', error)}
                                countryIsoCodeFilters={MarketCodeSchema.options}
                            />
                        )}
                    />

                    <Controller
                        name="role"
                        control={control}
                        render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                            <RoleSelector {...field} errorMessage={getErrorMessage('role', error)} />
                        )}
                    />
                </div>
            </form>
        </WizardPanel>
    );
};

interface ContactPanelFooterProps {
    onClick: () => void;
}

const ContactPanelFooter = (props: ContactPanelFooterProps) => {
    const { t } = useTranslation();

    return (
        <div className="flex justify-end">
            <Button label={t('common.save')} size="sm" type="button" onClick={props.onClick} />
        </div>
    );
};
